import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { Save, UploadCloud, MapPin, Phone, Instagram, Key, Star, Trash2, Globe, QrCode, Download } from 'lucide-react';
import QRCode from 'react-qr-code';
import { ALL_CITIES, PROVINCES, CITIES_BY_PROVINCE } from '../../data/locations';

interface AdminSedeConfigProps {
  userId: string;
  db: any;
  getCollectionPath: (col: string) => string;
  config?: any;
}

export function AdminSedeConfig({ userId, db, getCollectionPath, config }: AdminSedeConfigProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    photoUrl: '',
    address: '',
    location: '',
    phone: '',
    instagram: '',
    website: '',
    coverUrl: '',
    courtsCount: 1,
    city: ''
  });

  useEffect(() => {
    async function loadData() {
      try {
        const docSnap = await getDoc(doc(db, getCollectionPath('users'), userId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          let loc = data.location || '';
          if (loc === 'Río Negro' || loc === 'Rio Negro') loc = 'RN';
          
          setFormData({
            name: data.name || '',
            photoUrl: data.photoUrl || '',
            address: data.address || '',
            location: loc || 'RN',
            phone: data.phone || '',
            instagram: data.instagram || '',
            website: data.website || '',
            coverUrl: data.coverUrl || (data.galleryUrls && data.galleryUrls.length > 0 ? data.galleryUrls[0] : ''),
            courtsCount: data.courtsCount || 1,
            city: data.city || ''
          });
        }
      } catch (err: any) {
        setError('Error al cargar los datos de la sede.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [userId, db, getCollectionPath]);

  // Client-side image compression
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress as WEBP to preserve transparency
          const dataUrl = canvas.toDataURL('image/webp', 0.8);
          resolve(dataUrl);
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'photoUrl' | 'coverUrl') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressedBase64 = await compressImage(file);
      const imageRef = ref(storage, `clubs/${userId}_${field}_${Date.now()}.jpg`);
      await uploadString(imageRef, compressedBase64, 'data_url');
      const url = await getDownloadURL(imageRef);
      
      if (field === 'photoUrl') {
        setFormData({ ...formData, photoUrl: url });
      } else if (field === 'coverUrl') {
        setFormData({ ...formData, coverUrl: url });
      }
    } catch (err) {
      console.error(err);
      setError('Error al procesar la imagen.');
    }
  };

  const removeCoverImage = () => {
    setFormData({ ...formData, coverUrl: '' });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateDoc(doc(db, getCollectionPath('users'), userId), {
        name: formData.name,
        photoUrl: formData.photoUrl,
        address: formData.address,
        location: formData.location,
        phone: formData.phone,
        instagram: formData.instagram,
        website: formData.website,
        coverUrl: formData.coverUrl,
        courtsCount: formData.courtsCount,
        city: formData.city
      });
      setSuccess('Datos actualizados correctamente.');
    } catch (err: any) {
      console.error(err);
      setError('Error al guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center animate-pulse">Cargando datos de la sede...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-right-4 p-4 md:p-0">
      
      {/* Share Section */}
      <div className="bg-[#1a1c23] rounded-[var(--radius-large)] p-6 md:p-8 shadow-2xl relative space-y-6 flex flex-col md:flex-row items-center gap-8 border border-theme-primary/20">
         <div className="flex-1 space-y-4">
           <h3 className="text-xl font-black uppercase tracking-widest text-theme-primary flex items-center gap-3">
             <QrCode size={24} /> Compartir Página de Reservas
           </h3>
           <p className="text-xs text-zinc-400 whitespace-nowrap overflow-hidden text-ellipsis">
             Usa este enlace o QR para que tus jugadores puedan acceder <strong className="text-white">directamente</strong> a tu página de reservas.
           </p>
           <div className="flex gap-2 items-center">
             <div className="flex-1 bg-black/40 border border-white/10 rounded-xl p-3 font-mono text-xs text-white/70 overflow-hidden text-ellipsis whitespace-nowrap">
               https://rptpadel.com/club/{formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : userId}
             </div>
             <button 
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`https://rptpadel.com/club/${formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : userId}`);
                  alert('¡Enlace copiado!');
                }}
                className="bg-white/10 hover:bg-white/20 px-4 py-3 rounded-xl transition-colors text-xs font-bold uppercase tracking-widest text-white shrink-0"
             >
               Copiar
             </button>
           </div>
         </div>
         <div className="bg-white p-4 rounded-3xl flex flex-col items-center gap-4 shrink-0 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
           <QRCode 
             id="club-qr-code"
             value={`https://rptpadel.com/club/${formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : userId}`} 
             size={110}
             level="H"
             fgColor="#000000"
             bgColor="#ffffff"
           />
           <button
             type="button"
             onClick={async () => {
               const svg = document.getElementById("club-qr-code");
               if (!svg) return;
               
               const loadImage = (src: string) => {
                 return new Promise<HTMLImageElement>((resolve, reject) => {
                   const img = new Image();
                   img.crossOrigin = 'anonymous'; // Important for external URLs (Firebase Storage)
                   img.onload = () => resolve(img);
                   img.onerror = reject;
                   img.src = src;
                 });
               };

               try {
                 let svgData = new XMLSerializer().serializeToString(svg);
                 const qrImg = await loadImage("data:image/svg+xml;base64," + btoa(svgData));
                 
                 let clubLogoImg = null;
                 if (formData.photoUrl) {
                   try { clubLogoImg = await loadImage(formData.photoUrl); } catch (e) { console.error("Error loading club logo", e); }
                 }
                 
                 let bgImg = null;
                 try { bgImg = await loadImage('/qr-template.jpg'); } catch (e) { console.error("Error loading template", e); }

                 const canvas = document.createElement("canvas");
                 const ctx = canvas.getContext("2d");
                 if (!ctx) return;
                 
                 if (bgImg) {
                    canvas.width = bgImg.width;
                    canvas.height = bgImg.height;
                    ctx.drawImage(bgImg, 0, 0);
                 } else {
                    canvas.width = 758;
                    canvas.height = 1024;
                    ctx.fillStyle = '#15171d'; 
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                 }

                 // 1. QR Code inside the brackets
                 // The brackets are slightly above the center
                 const qrSize = Math.floor(canvas.width * 0.33);
                 const qrX = (canvas.width - qrSize) / 2;
                 const qrY = (canvas.height * 0.43) - (qrSize / 2); // Center of the brackets
                 
                 // White background for QR to be readable (sharp corners to match template)
                 ctx.fillStyle = '#ffffff';
                 ctx.fillRect(qrX - 16, qrY - 16, qrSize + 32, qrSize + 32);
                 
                 // Draw QR
                 ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

                 // 3. Club Logo in the CENTER of the QR
                 if (clubLogoImg) {
                    const logoSize = qrSize * 0.25; // 25% of the QR width
                    const logoX = qrX + (qrSize - logoSize) / 2;
                    const logoY = qrY + (qrSize - logoSize) / 2;
                    
                    // White circle behind logo to hide QR lines
                    ctx.fillStyle = '#ffffff';
                    ctx.beginPath();
                    ctx.arc(logoX + logoSize/2, logoY + logoSize/2, (logoSize/2) + 8, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, Math.PI * 2);
                    ctx.clip();
                    ctx.drawImage(clubLogoImg, logoX, logoY, logoSize, logoSize);
                    ctx.restore();
                 }
                 
                 const pngFile = canvas.toDataURL("image/png");
                 const downloadLink = document.createElement("a");
                 downloadLink.download = `QR_${formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') : 'club'}.png`;
                 downloadLink.href = pngFile;
                 downloadLink.click();

               } catch (e) {
                 console.error("Error generating QR", e);
                 alert("Hubo un error al generar la imagen. Es posible que el logo del club no permita ser descargado por políticas de seguridad del navegador.");
               }
             }}
             className="text-[9px] font-black uppercase tracking-widest text-black bg-[#eab308] hover:bg-[#d9a007] w-full py-2.5 px-3 rounded-xl transition-colors flex items-center justify-center gap-1.5"
           >
             <Download size={14} /> Descargar Tarjeta QR
           </button>
         </div>
      </div>

      <form onSubmit={handleSave} className="bg-[#1a1c23] rounded-[var(--radius-large)] p-6 md:p-8 shadow-2xl relative space-y-8">
        
        {/* Logo and Name */}
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="w-full md:w-1/3 flex flex-col items-center">
             <div className="w-40 h-40 rounded-full border-4 border-[#2d313f] mb-4 overflow-hidden relative group bg-[#15171d] flex items-center justify-center">
                {formData.photoUrl ? (
                 <img src={formData.photoUrl} alt="Logo" className="w-full h-full object-cover group-hover:opacity-50 transition-all" />
               ) : (
                 <UploadCloud size={40} className="opacity-30 text-theme-primary" />
               )}
               <label className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer bg-black/60 transition-all">
                  <span className="text-xs font-bold uppercase tracking-widest text-white text-center px-2">Cambiar Logo</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'photoUrl')} />
               </label>
             </div>
             <p className="text-[10px] opacity-40 uppercase tracking-widest text-center text-zinc-400">Recomendado: 1:1 Cuadrado</p>
          </div>

          <div className="w-full md:w-2/3 space-y-4">
             <div>
                <label className="block text-xs font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Nombre del Club</label>
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-2xl px-4 py-3.5 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-lg"
                />
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Teléfono Público</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 opacity-30 text-zinc-400" size={16} />
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-12 pr-4 py-3 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                    <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Instagram</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-extrabold text-white/85 select-none">
                        @
                      </span>
                      <input
                        type="text"
                        placeholder="tu usuario"
                        value={formData.instagram}
                        onChange={(e) => {
                          let val = e.target.value.trim();
                          if (val.startsWith('@')) {
                            val = val.substring(1);
                          }
                          if (val.includes('instagram.com/')) {
                            const parts = val.split('instagram.com/');
                            if (parts.length > 1) {
                              val = parts[1].split('/')[0].split('?')[0];
                            }
                          }
                          setFormData({...formData, instagram: val});
                        }}
                        className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-8 pr-4 py-3 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-sm placeholder:text-zinc-500"
                      />
                    </div>
                 </div>
              </div>
          </div>
        </div>

        {/* Location Info */}
        <div className="space-y-4 pt-6 border-t border-[#2d313f]">
           <h4 className="font-bold text-sm uppercase tracking-widest text-theme-primary mb-4 flex items-center gap-2">
             <MapPin size={16} /> Ubicación
           </h4>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Provincia</label>
                <select
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value, city: ''})}
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-sm cursor-pointer"
                >
                  <option value="">Seleccionar...</option>
                  {PROVINCES.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Ciudad</label>
                <select
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  disabled={!formData.location}
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-sm cursor-pointer disabled:opacity-50"
                >
                  <option value="">Seleccionar...</option>
                  {(CITIES_BY_PROVINCE[formData.location as keyof typeof CITIES_BY_PROVINCE] || []).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Dirección Física Exacta</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Ej: Av. San Lorenzo 1234"
                  className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl px-4 py-3 focus:border-theme-primary/50 outline-none transition-all font-bold text-white text-sm"
                />
              </div>
            </div>
        </div>

        {/* Cover and Courts */}
        <div className="space-y-4 pt-6 border-t border-[#2d313f]">
           <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-sm uppercase tracking-widest text-theme-primary flex items-center gap-2">
                    Imagen de Portada
                  </h4>
                  {!formData.coverUrl && (
                    <label className="cursor-pointer bg-[#222530] hover:bg-[#2d313f] px-4 py-2 rounded-lg text-xs font-bold transition-all border border-[#2d313f] text-zinc-200">
                      + Subir (Horizontal)
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'coverUrl')} />
                    </label>
                  )}
                </div>

                {!formData.coverUrl ? (
                  <div className="w-full h-40 border border-dashed border-[#2d313f] rounded-2xl flex flex-col items-center justify-center opacity-60">
                     <UploadCloud size={40} className="mb-2 text-zinc-500" />
                     <p className="text-sm font-bold text-zinc-400">Sin imagen</p>
                     <p className="text-xs text-zinc-500">Usa 1 foto horizontal (ej: tus canchas)</p>
                  </div>
                ) : (
                  <div className="relative w-full aspect-[21/9] rounded-xl overflow-hidden border border-[#2d313f] group bg-black/50">
                    <img src={formData.coverUrl} alt="Cover" className="w-full h-full object-cover" />
                    <button 
                      type="button"
                      onClick={removeCoverImage}
                      className="absolute top-2 right-2 bg-black/70 p-2 rounded-md text-red-400 opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="md:w-1/3">
                  <h4 className="font-bold text-sm uppercase tracking-widest text-theme-primary flex items-center gap-2 mb-4">
                   Nº de Canchas
                 </h4>
                 <div className="grid grid-cols-3 gap-2">
                   {[1, 2, 3, 4, 5, 6].map(num => (
                     <button
                       key={num}
                       type="button"
                       onClick={() => setFormData({...formData, courtsCount: num})}
                       className={`py-3 rounded-xl border font-black text-lg transition-all ${
                         formData.courtsCount === num 
                           ? 'theme-primary-bg text-black border-transparent scale-105' 
                           : 'bg-[#222530] border-[#2d313f] text-zinc-300 hover:bg-[#2d313f]'
                       }`}
                     >
                       {num}
                     </button>
                   ))}
                 </div>
              </div>
           </div>
        </div>

        {/* Actions */}
        <div className="pt-8 flex flex-col items-center gap-4">
          {error && <p className="text-red-400 text-sm font-bold bg-red-500/10 px-6 py-3 rounded-xl border border-red-500/20">{error}</p>}
          {success && <p className="text-emerald-400 text-sm font-bold bg-emerald-500/10 px-6 py-3 rounded-xl border border-emerald-500/20">{success}</p>}
          
          <button
            disabled={saving}
            type="submit"
            className="w-full md:w-auto min-w-[300px] theme-primary-bg text-black font-black px-10 py-4 rounded-2xl transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
          >
            {saving ? (
              <div className="w-5 h-5 border-4 border-black/20 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} />
                Guardar Perfil
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
